import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { capitalizeFirstLetter } from '../../../../../helpers/filterNames';
import { Chip } from '../../../../components/chip';
import { CHIP_COLOURS } from '../../../../lib/chip-colours';
import { cn } from '../../../../lib/utils';
import { useGetFiles } from '@/api/queries/useGetFiles';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { AudioReviewSummary } from '@/new/components/audio-speaker-tagging/audio-review-summary';
import { Icon } from '@/new/components/icon';
import type { IssueFormItem } from '@/new/components/matter/issues-list';
import type { PartyFormItem } from '@/new/components/matter/parties-list';

interface MatterCreatorProcessProps {
  issues: IssueFormItem[];
  parties: PartyFormItem[];
  additionalContext: string;
}

export function MatterCreatorProcess({ issues, parties, additionalContext }: MatterCreatorProcessProps) {
  const { matterId } = useParams();
  const { data: matter } = useGetMatterV2(matterId);
  const { data: files } = useGetFiles(matterId ?? '');

  const { t } = useTranslation();

  if (!matterId) return null;

  const fileCount = files?.length ?? 0;
  const pageCount = (files ?? []).reduce((total, file) => total + (file.numberOfPages ?? 0), 0);
  const missingName = !matter?.name?.trim();
  const missingIssues = issues.length === 0;
  const missingFiles = fileCount === 0;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-12 overflow-y-auto py-6 pb-32">
      {/* Documents */}
      <section className="w-full px-6">
        <div className="my-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-serif text-xl font-medium">Documents</h2>
            {missingFiles && (
              <p className="flex items-center gap-1.5 text-sm text-red-500">
                <Icon className="lucide--alert-circle" />
                At least one document is required.
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 text-gray-500">
            <p>
              {fileCount === 0 ? (
                'No documents uploaded.'
              ) : (
                <span>
                  <strong>{fileCount}</strong>
                  {` ${fileCount === 1 ? 'file' : 'files'}`}
                  {pageCount > 0 && (
                    <>
                      {' ('}
                      <strong>{pageCount.toLocaleString()}</strong>
                      {` ${pageCount === 1 ? 'page' : 'pages'})`}
                    </>
                  )}
                  {' waiting to be processed.'}
                </span>
              )}
            </p>
          </div>
          <AudioReviewSummary matterId={matterId} className="mt-4" />
        </div>
      </section>

      <hr />

      {/* Matter Info */}
      <section className="w-full px-6">
        <div className="my-8">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl font-medium">Basic Information</h2>
            {missingName && (
              <p className="flex items-center gap-1.5 text-sm text-red-500">
                <Icon className="lucide--alert-circle" />
                Matter name is required.
              </p>
            )}
          </div>
        </div>
        <dl className="space-y-3 text-sm">
          <div className="flex gap-3">
            <dt className="w-28 flex-shrink-0 text-gray-500">Name</dt>
            <dd className="text-gray-800">{matter?.name ?? '—'}</dd>
          </div>
          <div className="flex gap-3">
            <dt className="w-28 flex-shrink-0 text-gray-500">Code</dt>
            {matter?.code ? <dd className="text-gray-800">{matter.code}</dd> : <dd className="text-gray-400">Unset</dd>}
          </div>
          <div className="flex gap-3">
            <dt className="w-28 flex-shrink-0 text-gray-500">Page Limit</dt>
            {matter?.tokenLimit ? (
              <dd className="text-gray-800">{matter.tokenLimit.toLocaleString()}</dd>
            ) : (
              <dd className="text-gray-400">Unset</dd>
            )}
          </div>
        </dl>
      </section>

      <hr />

      {/* Issues */}
      <section className="w-full px-6">
        <div className="my-8">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl font-medium">Issues</h2>
            {missingIssues && (
              <p className="flex items-center gap-1.5 text-sm text-red-500">
                <Icon className="lucide--alert-circle" />
                At least one issue is required.
              </p>
            )}
          </div>
        </div>
        {issues.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {issues.map((issue, i) => (
              <li key={issue.id} className="flex items-start gap-3">
                <span className="flex-shrink-0 text-gray-400 tabular-nums">{i + 1}.</span>
                <div>
                  <span className="font-medium text-gray-800">{issue.label}</span>
                  {issue.text !== issue.label && <p className="mt-0.5 text-gray-500">{issue.text}</p>}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400">No issues added.</p>
        )}
      </section>

      <hr />

      {/* Parties */}
      <section className="w-full px-6">
        <div className="my-8">
          <h2 className="mb-4 font-serif text-xl font-medium">Parties</h2>
        </div>

        {parties.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {parties.map((party, i) => (
              <li key={party.id ?? `party-${i}`} className="flex items-center gap-1.5">
                <span className="flex-shrink-0 text-gray-400 tabular-nums">{i + 1}.</span>
                <span className="mr-1 text-gray-800">{party.name}</span>

                {party.role && party.role !== 'other' && (
                  <Chip className={cn(CHIP_COLOURS.default, 'border px-1.5 py-0.5 font-normal')}>
                    {party.role === 'claimant' ? capitalizeFirstLetter(t('claimant')) : 'Defendant'}
                  </Chip>
                )}

                {party.isClient && (
                  <Chip className={cn(CHIP_COLOURS.filters.person, 'border px-1.5 py-0.5 font-normal')}>Client</Chip>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400">No parties added.</p>
        )}
      </section>

      <hr />

      {/* Additional Context */}
      <section className="w-full px-6">
        <div className="my-8">
          <h2 className="mb-4 font-serif text-xl font-medium">Additional Context</h2>
        </div>
        {additionalContext ? (
          <p className="text-sm whitespace-pre-wrap text-gray-800">{additionalContext}</p>
        ) : (
          <p className="text-sm text-gray-400">No additional context provided.</p>
        )}
      </section>
    </div>
  );
}
